import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { GlobalUtils } from 'src/app/shared/global-utils';

@Component({
  selector: 'app-crud-select',
  templateUrl: './crud-select.component.html',
  styleUrls: ['./crud-select.component.sass'],
})
export class CrudSelectComponent implements OnInit, OnChanges {
  @Input()
  config: IFieldConfig;
  @Input()
  group: FormGroup;
  @Output()
  onSelect: EventEmitter<any> = new EventEmitter();
  @Output()
  onClick: EventEmitter<any> = new EventEmitter();
  @Input()
  typeCes?: TypeCesEnum | undefined;
  placeholder = 'Selecione uma opção';

  displayValue: string = '';
  selectedItem: any = null;
  private _displayValueCache: string = '';

  get displayValueForTemplate(): string {
    const currentValue = this.getDisplayValue();
    if (currentValue !== this._displayValueCache) {
      this._displayValueCache = currentValue;
      this.forceUpdate();
    }
    return currentValue;
  }

  get isRequired(): boolean {
    if (!this.config?.validation) return false;
    return this.config.validation.some(validator => {
      if (typeof validator === 'string') return validator === 'required';
      if (typeof validator === 'function') return validator.name === 'required';
      return false;
    });
  }

  constructor(public dialog: MatDialog, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    if (this.config.defaultValue)
      this.group
        .get(this.config.name)
        ?.setValue(
          this.config.defaultValue instanceof Array
            ? '(+1)'
            : this.config.defaultValue
        );

    setTimeout(() => {
      this.forceUpdate();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      console.log('CrudSelectComponent: Configuração atualizada', this.config);
    }
  }

  openSelectCrud() {
    if (
      this.typeCes === TypeCesEnum.SHOW ||
      this.group.get(this.config.name)?.disabled
    )
      return;

    if (this.config?.component && this.config.handleDirectly) {
      this.onClick.emit({ config: this.config, value: this.selectedItem });
      return;
    }

    // Aplicar transformação de dependência, se houver
    if (this.config.dependsOn) {
      const { field, getValue } = this.config.dependsOn;
      const dependentValue = this.group.get(field)?.value;

      if (dependentValue && getValue) {
        if (!this.config.componentData) {
          this.config.componentData = { params: {} };
        } else if (!this.config.componentData.params) {
          this.config.componentData.params = {};
        }

        const transformedValue = getValue(dependentValue);
        this.config.componentData.params.type = transformedValue;
      }
    }

    const params = this.config.componentData?.params || {};

    GlobalUtils.openDialog({
      dialog: this.dialog,
      component: this.config?.component,
      data: {
        type: TypeIndexEnum.SELECT,
        select: true,
        params: params
      },
      dialogConfig: {
        ...this.config.dialogConfig,
      },
      callBack: (data: any) => {
        if (data) {
          this.selectedItem = data;

          if (this.config.hideSelectedValue) {
            this.displayValue = data instanceof Array
              ? this.getText(data)
              : data[this.config.display || 'name'];

            const valueKey = this.config.value || 'id';
            const realValue = data instanceof Array
              ? data.map(item => item[valueKey])
              : data[valueKey];

            this.group.get(this.config.name)?.setValue(realValue);
          } else {
            const displayValue = data instanceof Array
              ? this.getText(data)
              : data[this.config.display || 'name'];

            // Armazenar o objeto completo no FormGroup
            this.group.get(this.config.name)?.setValue(data);
          }

          // Forçar detecção de mudanças após definir o valor
          this.forceUpdate();

          this.onSelect.emit(data);
        }
      },
    });

    this.forceUpdate();
  }

  get typeCesEnum() {
    return TypeCesEnum;
  }

  getText(data: any[]) {
    if (data.length > 1) {
      return (
        data.map((d) => d[this.config.display || 'name']).join(', ') +
        ` +(${data.length - 1})`
      );
    }

    return data.map((d) => d[this.config.display || 'name']).join(', ');
  }

  getDisplayValue(): string {
    if (this.config.displayValue && typeof this.config.displayValue === 'function') {
      return this.config.displayValue(this.selectedItem || this.group.get(this.config.name)?.value) || '';
    }

    if (this.config.hideSelectedValue && this.displayValue) {
      return this.displayValue;
    }

    const formValue = this.group.get(this.config.name)?.value;

    if (formValue && Array.isArray(formValue)) {
      if (formValue.length > 1) {
        const result = formValue.map(item => item[this.config.display || 'name']).join(', ') + ` +(${formValue.length - 1})`;
        return result;
      } else if (formValue.length === 1) {
        const result = formValue[0][this.config.display || 'name'] || '';
        return result;
      }
      return '';
    }

    if (formValue && typeof formValue === 'object' && formValue[this.config.display || 'name']) {
      const result = formValue[this.config.display || 'name'];
      return result;
    }

    if (typeof formValue === 'string' && formValue.trim() !== '') {
      return formValue;
    }

    if (this.selectedItem) {
      const result = this.selectedItem[this.config.display || 'name'] || '';
      return result;
    }
    return '';
  }

  private forceUpdate(): void {
    this.cdr.detectChanges();
  }

  public clear() {
    if (this.group.get(this.config.name)?.disabled) {
      return;
    }

    this.group.get(this.config.name)?.setValue(null);
    this.displayValue = '';
    this.selectedItem = null;
    this.forceUpdate();
    setTimeout(() => {
      this.onSelect.emit(null);
    }, 0);
  }
}
